/**
 * Cliente REST de la API de CLASES EN VIVO (F3-G). Envuelve los endpoints del
 * plano de control (`/api/live/*`). Usa el mismo transporte autenticado que la
 * API de media (`mediaApiFetch`: cookie-BFF en web, Bearer inyectado en Rust en
 * native), así que NO depende del cliente tipado openapi (evita regenerarlo).
 *
 * `meeting_config` es SERVER-ONLY: el join solo devuelve lo que el cliente puede
 * ver (dominio+sala para embed, o URL para externo).
 */
import { mediaApiFetch } from "@/lib/api";

/** Datos de embebido (Jitsi): dominio del servidor + sala determinista. */
export interface JoinEmbed {
  domain: string;
  room: string;
}

/** Respuesta de `GET /api/live/sessions/:id/join` cuando la ventana está abierta. */
export interface JoinResponse {
  provider: string;
  mode: "embed" | "external";
  joinUrl?: string;
  embed?: JoinEmbed;
  startsAt: string;
  endsAt: string;
  topic?: string;
  cohortName: string;
}

/** Resultado del join: éxito, o rechazo por ventana/cancelación (403 con motivo). */
export type JoinResult =
  | { ok: true; data: JoinResponse }
  | { ok: false; reason: string; opensAt: string | null };

/** Error de red/servidor NO esperado (distinto del 403 de ventana, que es dato). */
class LiveApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
  ) {
    super(message);
    this.name = "LiveApiError";
  }
}

export type LiveFetch = (path: string, init?: RequestInit) => Promise<Response>;

export interface LiveApi {
  /** Resuelve cómo unirse; el 403 de ventana/cancelación se devuelve como dato. */
  join(sessionId: string): Promise<JoinResult>;
  /** Profesor/admin: fija proveedor + config de UNA sesión. */
  setMeeting(sessionId: string, provider: string, config: Record<string, unknown>): Promise<void>;
  /** Profesor/admin: default de proveedor a nivel COHORTE. */
  setCohortDefaults(
    cohortId: string,
    provider: string,
    config: Record<string, unknown>,
  ): Promise<void>;
  /** Profesor/admin: enlaza una grabación (video_asset) a la sesión. */
  attachRecording(sessionId: string, videoAssetId: string): Promise<void>;
}

function enc(s: string): string {
  return encodeURIComponent(s);
}

async function expectOk(res: Response, ctx: string): Promise<void> {
  if (!res.ok) {
    const body = await res.text().catch(() => "");
    // eslint-disable-next-line lingui/no-unlocalized-strings -- diagnóstico interno del transporte, no texto de UI
    throw new LiveApiError(`${ctx}: HTTP ${res.status} ${body}`.trim(), res.status);
  }
}

function createLiveApi(fetchImpl: LiveFetch = mediaApiFetch): LiveApi {
  return {
    async join(sessionId) {
      const res = await fetchImpl(`/api/live/sessions/${enc(sessionId)}/join`);
      if (res.status === 403) {
        // 403 de ventana/cancelación: cuerpo { reason, opensAt } — es DATO, no error.
        // Lectura en el trust-boundary del servidor; el llamador valida lo que usa.
        // oxlint-disable-next-line typescript/no-unsafe-type-assertion
        const body = (await res.json().catch(() => ({}))) as {
          reason?: string;
          opensAt?: string | null;
        };
        return { ok: false, reason: body.reason ?? "forbidden", opensAt: body.opensAt ?? null };
      }
      await expectOk(res, "join");
      // Trust-boundary read; el consumidor valida lo que usa.
      // oxlint-disable-next-line typescript/no-unsafe-type-assertion
      return { ok: true, data: (await res.json()) as JoinResponse };
    },

    async setMeeting(sessionId, provider, config) {
      const res = await fetchImpl(`/api/live/sessions/${enc(sessionId)}/meeting`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ provider, config }),
      });
      await expectOk(res, "setMeeting");
    },

    async setCohortDefaults(cohortId, provider, config) {
      const res = await fetchImpl(`/api/live/cohorts/${enc(cohortId)}/meeting-defaults`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ provider, config }),
      });
      await expectOk(res, "setCohortDefaults");
    },

    async attachRecording(sessionId, videoAssetId) {
      const res = await fetchImpl(`/api/live/sessions/${enc(sessionId)}/recording`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ videoAssetId }),
      });
      await expectOk(res, "attachRecording");
    },
  };
}

/** Instancia por defecto (transporte real). */
export const liveApi: LiveApi = createLiveApi();
